import React, { useCallback, useEffect, useState } from 'react';
import styles from './Market.module.scss';
import { apiService } from '@/services/api';
import type { MarketListingSummary } from '@/types/market';

export const DEFAULT_AVATAR =
  'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200';

/** Human price: "Contact for price" when no price set, else currency + amount. */
export function formatPrice(price?: number | null, currency?: string | null): string {
  if (price === null || price === undefined || Number.isNaN(Number(price))) {
    return 'Contact for price';
  }
  const cur = currency || 'AED';
  try {
    return `${cur} ${Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  } catch {
    return `${cur} ${price}`;
  }
}

export function timeAgo(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Read-only star rating (out of 5). */
export function Stars({ value, count }: { value?: number | null; count?: number | null }) {
  const v = Math.round((value || 0) * 2) / 2;
  const full = Math.floor(v);
  const half = v - full >= 0.5;
  const stars = '★'.repeat(full) + (half ? '½' : '');
  const empty = '☆'.repeat(Math.max(0, 5 - full - (half ? 1 : 0)));
  return (
    <span className={styles.ratingRow}>
      <span className={styles.stars}>
        {stars}
        <span className={styles.starsEmpty}>{empty}</span>
      </span>
      {typeof count === 'number' && <span>({count})</span>}
    </span>
  );
}

/**
 * Shared saved-ads state for list screens. Fetches the viewer's saved ids once,
 * exposes a Set + an optimistic toggle (reverts on error).
 */
export function useSavedIds() {
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    apiService
      .mktGetSavedIds()
      .then((ids) => { if (alive) setSavedIds(new Set(ids)); })
      .catch(() => { /* non-fatal: default to none saved */ });
    return () => { alive = false; };
  }, []);

  const toggleSave = useCallback((id: string) => {
    let wasSaved = false;
    setSavedIds((prev) => {
      wasSaved = prev.has(id);
      const next = new Set(prev);
      if (wasSaved) next.delete(id); else next.add(id);
      return next;
    });
    const p = wasSaved ? apiService.mktUnsaveListing(id) : apiService.mktSaveListing(id);
    p.catch(() => {
      // Revert on failure.
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(id); else next.delete(id);
        return next;
      });
    });
  }, []);

  return { savedIds, toggleSave };
}

/** A single listing card used across Home / Browse / Saved / provider profiles. */
export function ListingCard({
  listing,
  onClick,
  saved,
  onToggleSave,
}: {
  listing: MarketListingSummary;
  onClick: () => void;
  saved?: boolean;
  onToggleSave?: () => void;
}) {
  return (
    <div className={styles.card} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      {onToggleSave && (
        <button
          type="button"
          className={`${styles.savedBtn} ${saved ? styles.savedBtnActive : ''}`}
          aria-label={saved ? 'Remove from saved ads' : 'Save this ad'}
          aria-pressed={!!saved}
          onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
        >
          {saved ? '♥' : '♡'}
        </button>
      )}
      {listing.thumbnail ? (
        <img className={styles.cardThumb} src={listing.thumbnail} alt={listing.title}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
      ) : (
        <div className={styles.cardThumbPlaceholder}>{listing.kind === 'service' ? '🛠️' : '📦'}</div>
      )}
      <div className={styles.cardBody}>
        <span className={styles.cardPrice}>{formatPrice(listing.price, listing.currency)}</span>
        <span className={styles.cardTitle}>{listing.title}</span>
        <span className={styles.cardMeta}>
          <span>{listing.city || '—'}</span>
          <span>{timeAgo(listing.createdAt)}</span>
        </span>
      </div>
    </div>
  );
}
